import React from 'react'
import {Card, CardContent, Typography, Box} from '@mui/material'
import {makeStyles} from '@oc/styles'
import {Build} from '@mui/icons-material'
import {useViewLoader} from '@oc/shell-core'
import {ManualPromise} from '@oc/utils'

const useStyles = makeStyles({
    root: {
        minWidth: 275
    },
    title: {
        fontSize: 14
    },
    icon: {
        marginRight: 12
    },
    flexBox: {
        display: 'flex',
        alignItems: 'center'
    },
    rootFlexBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%'
    },
    rootFlexBoxRestricted: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%'
    },
    fullscreen: {
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
})

interface WhileLoadingProps {
    message?: string
    coverWholePage?: boolean
}

function WhileLoading(props: WhileLoadingProps) {
    const viewLoader = useViewLoader()

    React.useEffect(() => {
        const loading = new ManualPromise()
        viewLoader.whileLoading(loading, props.message, props.coverWholePage)

        return loading.resolve
    }, [viewLoader, props])

    return null
}

interface ErrorBoundaryComponentProps extends React.PropsWithChildren {
    classes: ReturnType<typeof useStyles>
    fallbackChildren?: React.ReactNode
}

interface ErrorBoundaryComponentState {
    hasError: boolean
}

class ErrorBoundaryComponent extends React.PureComponent<ErrorBoundaryComponentProps, ErrorBoundaryComponentState> {
    public override state = {
        hasError: false
    }

    public override componentDidCatch() {
        this.setState({hasError: true})
    }

    static getDerivedStateFromError() {
        return {hasError: true}
    }

    public override render() {
        const {
            props: {
                children,
                classes: {classes},
                fallbackChildren
            },
            state: {hasError}
        } = this

        if (hasError) {
            if (fallbackChildren) {
                return fallbackChildren
            }

            return (
                <Box className={classes.rootFlexBox}>
                    <Card className={classes.root}>
                        <CardContent>
                            <Box className={classes.flexBox}>
                                <Box className={classes.icon}>
                                    <Build />
                                </Box>
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Requested module not found.
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                        Refresh the page please. If this behaviour persists please contact
                                        administrator.
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )
        }

        return children
    }
}

interface ErrorBoundaryProps extends React.PropsWithChildren {
    fallbackChildren?: React.ReactNode
}

function ErrorBoundary(props: ErrorBoundaryProps) {
    const classes = useStyles()
    return (
        <ErrorBoundaryComponent classes={classes} fallbackChildren={props.fallbackChildren}>
            {props.children}
        </ErrorBoundaryComponent>
    )
}

type SuspenseWhileLoadingProps<T extends object> = {
    renderChildrenOnError?: boolean
    whileLoadingProps?: WhileLoadingProps
    LazyComponent: React.LazyExoticComponent<(props: T) => React.JSX.Element | null>
} & (keyof T extends never
    ? {
          lazyComponentProps?: T
      }
    : {
          lazyComponentProps: T
      })

export function SuspenseWhileLoading<T extends object>(props: SuspenseWhileLoadingProps<T>) {
    const {LazyComponent, lazyComponentProps, renderChildrenOnError} = props

    // HACK: Typescript insists on handling passing "ref", even it will never actually be passed. Disabled typechecking of props
    const LazyComponentAny = LazyComponent as React.LazyExoticComponent<(props: any) => React.JSX.Element>

    return (
        <ErrorBoundary
            fallbackChildren={
                renderChildrenOnError && lazyComponentProps && 'children' in lazyComponentProps
                    ? (lazyComponentProps['children'] as React.ReactNode)
                    : undefined
            }
        >
            <React.Suspense fallback={<WhileLoading {...props.whileLoadingProps} />}>
                <LazyComponentAny {...lazyComponentProps} />
            </React.Suspense>
        </ErrorBoundary>
    )
}
